import { useState, useEffect } from 'react';
import { FileText, Code, Search, Database, Edit, History, Trash2, Plus, Save, X, BookOpen, Lightbulb, HelpCircle } from 'lucide-react';
import api from '../utils/api';
import InfoTooltip from './InfoTooltip';
import { SETTING_TOOLTIPS, AVAILABLE_MODELS, TEMPLATE_TYPES, DEFAULT_FORM_VALUES } from './promptConstants';
import { validateForm, validateTemperature, validateTopP, validateMaxTokens } from './promptValidation';

const Prompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_VALUES });
  const [errors, setErrors] = useState({});
  const [newStopSequence, setNewStopSequence] = useState('');

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await api.getPrompts();
      setPrompts(response.prompts || []);
    } catch (error) {
      console.log('[VISION] Failed to fetch prompts:', error);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset form to defaults
  const resetForm = () => {
    setFormData({ ...DEFAULT_FORM_VALUES });
    setErrors({});
    setNewStopSequence('');
  };

  // Update inference configuration helper
  const updateInferenceConfig = (field, value) => {
    setFormData(prev => ({
      ...prev,
      inferenceConfiguration: {
        text: {
          ...prev.inferenceConfiguration.text,
          [field]: value
        }
      }
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate field on blur
  const handleFieldBlur = (field, value) => {
    let result;
    switch (field) {
      case 'temperature':
        result = validateTemperature(value);
        break;
      case 'topP':
        result = validateTopP(value);
        break;
      case 'maxTokens':
        result = validateMaxTokens(value);
        break;
      default:
        return;
    }
    
    if (!result.isValid) {
      setErrors(prev => ({ ...prev, [field]: result.error }));
    } else {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Add stop sequence
  const addStopSequence = () => {
    if (!newStopSequence.trim()) return;
    const currentSequences = formData.inferenceConfiguration.text.stopSequences || [];
    if (currentSequences.length >= 4) {
      setErrors(prev => ({ ...prev, stopSequences: 'Maximum 4 stop sequences allowed' }));
      return;
    }
    updateInferenceConfig('stopSequences', [...currentSequences, newStopSequence.trim()]);
    setNewStopSequence('');
    setErrors(prev => ({ ...prev, stopSequences: null }));
  };

  // Remove stop sequence
  const removeStopSequence = (index) => {
    const currentSequences = formData.inferenceConfiguration.text.stopSequences || [];
    updateInferenceConfig('stopSequences', currentSequences.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    const validation = validateForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    try {
      await api.createPrompt(formData);
      setShowCreateForm(false);
      resetForm();
      fetchPrompts();
    } catch (error) {
      alert('Failed to create prompt: ' + error.message);
    }
  };

  const handleUpdate = async (promptId) => {
    const validation = validateForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    try {
      await api.updatePrompt(promptId, formData);
      setEditingPrompt(null);
      resetForm();
      fetchPrompts();
    } catch (error) {
      alert('Failed to update prompt: ' + error.message);
    }
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    
    try {
      await api.deletePrompt(promptId);
      fetchPrompts();
    } catch (error) {
      alert('Failed to delete prompt: ' + error.message);
    }
  };

  // Start editing - populate form with all settings from variant
  const startEdit = (prompt) => {
    setEditingPrompt(prompt.id);
    const variant = prompt.variants && prompt.variants[0];
    const inferenceConfig = variant?.inferenceConfiguration?.text || {};
    
    setFormData({
      name: prompt.name || prompt.title || '',
      description: prompt.description || '',
      templateType: variant?.templateType || 'TEXT',
      templateConfiguration: variant?.templateConfiguration || { text: { text: prompt.description || '' } },
      modelId: variant?.modelId || DEFAULT_FORM_VALUES.modelId,
      inferenceConfiguration: {
        text: {
          temperature: inferenceConfig.temperature ?? DEFAULT_FORM_VALUES.inferenceConfiguration.text.temperature,
          topP: inferenceConfig.topP ?? DEFAULT_FORM_VALUES.inferenceConfiguration.text.topP,
          maxTokens: inferenceConfig.maxTokens ?? DEFAULT_FORM_VALUES.inferenceConfiguration.text.maxTokens,
          stopSequences: inferenceConfig.stopSequences || []
        }
      }
    });
    setErrors({});
  };

  const getIcon = (iconType) => {
    switch(iconType) {
      case 'file': return <FileText size={20} />;
      case 'code': return <Code size={20} />;
      case 'search': return <Search size={20} />;
      case 'database': return <Database size={20} />;
      default: return <FileText size={20} />;
    }
  };

  // Render the prompt form (used for both create and edit)
  const renderPromptForm = (isCreate = false, promptId = null) => (
    <div className="space-y-4">
      {/* Basic Info Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Name Field */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label className="text-sm text-text-secondary">Name</label>
            <InfoTooltip content={SETTING_TOOLTIPS.name} position="right" />
          </div>
          <input
            type="text"
            placeholder="Prompt Name"
            value={formData.name}
            onChange={(e) => {
              setFormData({...formData, name: e.target.value});
              if (errors.name) setErrors(prev => ({ ...prev, name: null }));
            }}
            className={`w-full bg-dark-border/40 border rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none ${errors.name ? 'border-red-500' : 'border-border/60'}`}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Model Selection */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label className="text-sm text-text-secondary">Model</label>
            <InfoTooltip content={SETTING_TOOLTIPS.modelId} position="right" />
          </div>
          <select
            value={formData.modelId}
            onChange={(e) => setFormData({...formData, modelId: e.target.value})}
            className="w-full bg-dark-border/40 border border-border/60 rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none">
            {AVAILABLE_MODELS.map(model => (
              <option key={model.id} value={model.id}>{model.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Description Field */}
      <div>
        <div className="flex items-center gap-1 mb-1">
          <label className="text-sm text-text-secondary">Description</label>
          <InfoTooltip content={SETTING_TOOLTIPS.description} position="right" />
        </div>
        <input
          type="text"
          placeholder="Description"
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full bg-dark-border/40 border border-border/60 rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none"
        />
      </div>

      {/* Template Type Selection */}
      <div>
        <div className="flex items-center gap-1 mb-1">
          <label className="text-sm text-text-secondary">Template Type</label>
          <InfoTooltip content={SETTING_TOOLTIPS.templateType} position="right" />
        </div>
        <select
          value={formData.templateType}
          onChange={(e) => setFormData({...formData, templateType: e.target.value})}
          className="w-full bg-dark-border/40 border border-border/60 rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none">
          {TEMPLATE_TYPES.map(type => (
            <option key={type.id} value={type.id}>{type.name} - {type.description}</option>
          ))}
        </select>
      </div>

      {/* Template Content */}
      <div>
        <div className="flex items-center gap-1 mb-1">
          <label className="text-sm text-text-secondary">Prompt Template</label>
          <InfoTooltip content={SETTING_TOOLTIPS.template} position="right" />
        </div>
        <textarea
          placeholder="Enter your prompt template..."
          value={formData.templateConfiguration.text.text}
          onChange={(e) => {
            setFormData({...formData, templateConfiguration: { text: { text: e.target.value } }});
            if (errors.template) setErrors(prev => ({ ...prev, template: null }));
          }}
          rows={6}
          className={`w-full bg-dark-border/40 border rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none font-mono text-sm ${errors.template ? 'border-red-500' : 'border-border/60'}`}
        />
        {errors.template && <p className="text-red-500 text-xs mt-1">{errors.template}</p>}
      </div>

      {/* Inference Configuration Section */}
      <div className="border-t border-border/40 pt-4 mt-4">
        <h4 className="text-sm font-semibold text-emerald-400 mb-3">Inference Configuration</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Temperature */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm text-text-secondary">Temperature</label>
              <InfoTooltip content={SETTING_TOOLTIPS.temperature} position="top" />
            </div>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={formData.inferenceConfiguration.text.temperature}
              onChange={(e) => updateInferenceConfig('temperature', parseFloat(e.target.value) || 0)}
              onBlur={(e) => handleFieldBlur('temperature', e.target.value)}
              className={`w-full bg-dark-border/40 border rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none ${errors.temperature ? 'border-red-500' : 'border-border/60'}`}
            />
            {errors.temperature && <p className="text-red-500 text-xs mt-1">{errors.temperature}</p>}
          </div>

          {/* Top P */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm text-text-secondary">Top P</label>
              <InfoTooltip content={SETTING_TOOLTIPS.topP} position="top" />
            </div>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={formData.inferenceConfiguration.text.topP}
              onChange={(e) => updateInferenceConfig('topP', parseFloat(e.target.value) || 0)}
              onBlur={(e) => handleFieldBlur('topP', e.target.value)}
              className={`w-full bg-dark-border/40 border rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none ${errors.topP ? 'border-red-500' : 'border-border/60'}`}
            />
            {errors.topP && <p className="text-red-500 text-xs mt-1">{errors.topP}</p>}
          </div>

          {/* Max Tokens */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm text-text-secondary">Max Tokens</label>
              <InfoTooltip content={SETTING_TOOLTIPS.maxTokens} position="top" />
            </div>
            <input
              type="number"
              min="1"
              max="200000"
              value={formData.inferenceConfiguration.text.maxTokens}
              onChange={(e) => updateInferenceConfig('maxTokens', parseInt(e.target.value) || 1)}
              onBlur={(e) => handleFieldBlur('maxTokens', e.target.value)}
              className={`w-full bg-dark-border/40 border rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none ${errors.maxTokens ? 'border-red-500' : 'border-border/60'}`}
            />
            {errors.maxTokens && <p className="text-red-500 text-xs mt-1">{errors.maxTokens}</p>}
          </div>
        </div>

        {/* Stop Sequences */}
        <div className="mt-4">
          <div className="flex items-center gap-1 mb-1">
            <label className="text-sm text-text-secondary">Stop Sequences</label>
            <InfoTooltip content={SETTING_TOOLTIPS.stopSequences} position="right" />
          </div>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Add stop sequence..."
              value={newStopSequence}
              onChange={(e) => setNewStopSequence(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStopSequence())}
              className="flex-1 bg-dark-border/40 border border-border/60 rounded px-3 py-2 text-text-primary focus:border-lime-accent/40 focus:outline-none text-sm"
            />
            <button
              type="button"
              onClick={addStopSequence}
              className="px-3 py-2 rounded bg-lime-accent/20 border border-lime-accent/40 text-emerald-400 hover:bg-lime-accent/30 transition-all text-sm"
            >
              Add
            </button>
          </div>
          {errors.stopSequences && <p className="text-red-500 text-xs mb-2">{errors.stopSequences}</p>}
          <div className="flex flex-wrap gap-2">
            {(formData.inferenceConfiguration.text.stopSequences || []).map((seq, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-500/20 border border-blue-500/40 text-blue-400 text-xs"
              >
                <code>{seq}</code>
                <button
                  type="button"
                  onClick={() => removeStopSequence(index)}
                  className="hover:text-red-400 transition-colors"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        <button 
          onClick={isCreate ? handleCreate : () => handleUpdate(promptId)} 
          className="flex items-center gap-2 px-4 py-2 rounded bg-lime-accent/20 border border-lime-accent/40 text-emerald-400 hover:bg-lime-accent/30 transition-all font-semibold"
        >
          <Save size={16} />
          {isCreate ? 'Create' : 'Save'}
        </button>
        <button 
          onClick={() => {
            if (isCreate) {
              setShowCreateForm(false);
            } else {
              setEditingPrompt(null);
            }
            resetForm();
          }} 
          className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-text-primary transition-all"
        >
          <X size={16} />
          Cancel
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-emerald-400 text-lg">Loading prompts...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-emerald-400">
          Prompts
        </h1>
        <button 
          onClick={() => {
            resetForm();
            setShowCreateForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 rounded bg-lime-accent/20 border border-lime-accent/40 text-emerald-400 hover:bg-lime-accent/30 transition-all font-semibold"
          >
          <Plus size={16} />
          New Prompt
        </button>
      </div>

      {/* Instructions Box for Non-Technical Users */}
      <div className="bg-surface rounded-lg border border-border p-6"
           >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-lime-accent/20 border border-lime-accent/40 flex items-center justify-center flex-shrink-0">
            <BookOpen size={20} className="text-emerald-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-text-primary mb-3">What are Prompts?</h2>
            <p className="text-text-primary text-sm mb-4">
              Prompts are instructions you give to the AI to guide its responses. Think of them as templates that tell the AI what kind of answer you want and how it should behave.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex items-start gap-3">
                <Lightbulb size={18} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-text-primary mb-1">Getting Started</h3>
                  <p className="text-xs text-text-secondary">Click "New Prompt" to create your first prompt. Give it a name, write your instructions in the template box, and save.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <HelpCircle size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-text-primary mb-1">Need Help?</h3>
                  <p className="text-xs text-text-secondary">Hover over the <span className="text-emerald-400">ⓘ</span> icons next to each setting to learn what it does and how to use it.</p>
                </div>
              </div>
            </div>

            <details className="group">
              <summary className="cursor-pointer text-sm text-emerald-400 hover:text-emerald-400/80 transition-colors flex items-center gap-2">
                <span>Understanding Key Settings</span>
                <span className="text-xs text-text-muted">(click to expand)</span>
              </summary>
              <div className="mt-3 pl-4 border-l-2 border-lime-accent/30 space-y-3">
                <div>
                  <span className="text-sm font-medium text-blue-400">Temperature (0-1):</span>
                  <span className="text-xs text-text-secondary ml-2">Controls creativity. Low = focused and consistent. High = creative and varied.</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-blue-400">Top P (0-1):</span>
                  <span className="text-xs text-text-secondary ml-2">Controls word variety. Lower = more predictable. Higher = more diverse vocabulary.</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-blue-400">Max Tokens:</span>
                  <span className="text-xs text-text-secondary ml-2">Maximum response length. 1 token ≈ 4 characters. Higher = longer responses.</span>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-surface rounded-lg border border-lime-accent/40 p-6"
             >
          <h3 className="text-lg font-bold text-emerald-400 mb-4">Create New Prompt</h3>
          {renderPromptForm(true)}
        </div>
      )}

      {/* Prompts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {prompts.map((prompt) => (
          <div key={prompt.id} 
               className="bg-surface rounded-lg border border-border p-6 hover:border-lime-accent/40 transition-all group"
               >
            
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded bg-blue-500/20 border border-blue-500/40 flex items-center justify-center flex-shrink-0 text-blue-400"
                   >
                {getIcon(prompt.icon)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-text-primary">{prompt.name || prompt.title}</h3>
                  {prompt.version && (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-500/20 border border-blue-500/40 text-blue-400">
                      {prompt.version}
                    </span>
                  )}
                </div>
                {prompt.subtitle && (
                  <p className="text-sm text-text-secondary">{prompt.subtitle}</p>
                )}
              </div>
            </div>

            {prompt.id_label && (
              <p className="text-xs text-text-muted mb-2">{prompt.id_label}</p>
            )}

            {/* Show inference config summary if available */}
            {prompt.variants && prompt.variants[0]?.inferenceConfiguration?.text && (
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-xs px-2 py-1 rounded bg-dark-border/60 text-text-secondary">
                  Temp: {prompt.variants[0].inferenceConfiguration.text.temperature ?? 'default'}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-dark-border/60 text-text-secondary">
                  TopP: {prompt.variants[0].inferenceConfiguration.text.topP ?? 'default'}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-dark-border/60 text-text-secondary">
                  Max: {prompt.variants[0].inferenceConfiguration.text.maxTokens ?? 'default'}
                </span>
              </div>
            )}

            {(prompt.description || (prompt.variants && prompt.variants[0]?.templateConfiguration?.text?.text)) && (
              <div className="bg-dark-border/40 border border-border/60 rounded p-3 mb-4 font-mono text-xs text-text-primary leading-relaxed max-h-32 overflow-hidden">
                {prompt.description || prompt.variants[0]?.templateConfiguration?.text?.text}
              </div>
            )}

            {prompt.updated && (
              <p className="text-xs text-text-muted mb-4">{prompt.updated}</p>
            )}

            {editingPrompt === prompt.id ? (
              renderPromptForm(false, prompt.id)
            ) : (
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => startEdit(prompt)}
                  className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-lime-accent/40 text-emerald-400 hover:bg-lime-accent/10 transition-all text-sm font-semibold"
                  >
                  <Edit size={14} />
                  Edit
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 transition-all text-sm font-semibold">
                  <History size={14} />
                  History
                </button>
                <button 
                  onClick={() => handleDelete(prompt.id)}
                  className="ml-auto p-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-red-500 hover:border-red-500/40 transition-all">
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Prompts;
